import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { fetchCompanyFeatures } from '../services/companyCustomizationApi';
import type { CompanyFeature } from '../services/companyCustomizationApi';
import { useCompany } from './CompanyContext';

type CompanyFeaturesContextValue = {
  features: CompanyFeature[];
  isLoading: boolean;
  refresh: () => Promise<void>;
  hasFeature: (key: string, defaultValue?: boolean) => boolean;
};

const defaultCompanyFeaturesContext: CompanyFeaturesContextValue = {
  features: [],
  isLoading: false,
  refresh: async () => undefined,
  hasFeature: (_key: string, defaultValue = true) => defaultValue,
};

const CompanyFeaturesContext = createContext<CompanyFeaturesContextValue>(defaultCompanyFeaturesContext);

export const CompanyFeaturesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentCompany } = useCompany();
  const [features, setFeatures] = useState<CompanyFeature[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadFeatures = useCallback(async () => {
    if (!currentCompany) {
      setFeatures([]);
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchCompanyFeatures(currentCompany.id);
      setFeatures(data);
    } catch (error) {
      console.error('[CompanyFeaturesProvider] Failed to load features', error);
      setFeatures([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentCompany]);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  const hasFeature = useCallback(
    (key: string, defaultValue = true) => {
      const feature = features.find((item) => item.featureKey === key);
      return feature ? feature.enabled : defaultValue;
    },
    [features],
  );

  const value = useMemo<CompanyFeaturesContextValue>(
    () => ({
      features,
      isLoading,
      refresh: loadFeatures,
      hasFeature,
    }),
    [features, isLoading, loadFeatures, hasFeature],
  );

  return <CompanyFeaturesContext.Provider value={value}>{children}</CompanyFeaturesContext.Provider>;
};

export const useCompanyFeatures = (): CompanyFeaturesContextValue => {
  return useContext(CompanyFeaturesContext);
};

